import React, { useContext, useState, useEffect } from 'react';
import logo from "apm/assets/img/logo.png";
import useAuth from "apm/contexts/useAuth";
import ProjectContext from "apm/contexts/ProjectContext";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { pendingActionsCount } from "apm/api/pending_action";
import { Badge, Box, IconButton } from "@mui/material";
import { Navbar, Nav } from 'rsuite';
import "./styles/Header.css";
import PendingActionPopup from "../private/PendingActionPopup";
import { Roles } from "apm/utils/roles";

export default function Header() {
    const project = useContext(ProjectContext);
    const userRoles = project.userRoles;
    const { logout } = useAuth();
    const location = useLocation();

    // @ts-ignore
    const NavLink = React.forwardRef(({ href, children, ...rest }, ref) => (
        // @ts-ignore
        <Link className='font-weight-bold' ref={ref} to={href} {...rest}>
            {children}
        </Link>
    ));

    function Logo() {
        return (
            <span className="avatar avatar-xxl navbar-brand-icon ml-3 mr-lg-8pt">
                <span className="avatar-title">
                    <div style={{ borderRight: '1px solid #FFFFFF', marginTop: '-4px', paddingRight: '9px' }}>
                        <img src={logo} width="165" alt="logo"
                            className="" />
                    </div>
                </span>
            </span>
        )
    }

    function TopLeftSideBar() {
        return (
            <>
                <a href={process.env.REACT_APP_VERACITY_BASE_URL} className="navbar-brand mr-16pt">
                    <Logo />
                </a>
                <Nav className="nav navbar-nav d-none d-sm-flex flex justify-content-start ml-3"
                    activeKey={location.pathname}>
                    <Nav.Item as={NavLink} href={'/'}>Home</Nav.Item>
                    <Nav.Item as={NavLink} href={'/projects'}>Projects</Nav.Item>
                    {project && <Nav.Item as={NavLink} href={`/projects/${project.id}/executive-dashboard`}>
                        {project.name}</Nav.Item>}
                </Nav>
            </>
        );
    }

    function TopRightSideBar() {

        const [profileOpen, setProfileOpen] = useState(false);
        const { user } = useAuth();
        const [pendingActionOpen, setPendingActionOpen] = useState(false);
        const handleProfileOpen = () => {
            setProfileOpen(!profileOpen);
        };
        const handleLogout = (e: React.MouseEvent<Element, MouseEvent>) => {
            e.preventDefault();
            logout();
        }
        const { data: pendingActions } = useQuery<number>(["pendingCount"], pendingActionsCount, {
            staleTime: 1200000,
            cacheTime: 1200000,
        });


        function notificationsLabel(count: number) {
            if (count === 0) {
                return 'no notifications';
            }
            if (count > 99) {
                return 'more than 99 notifications';
            }
            return `${count} notifications`;
        }

        return (
            <div className="nav navbar-nav flex-nowrap d-flex mr-16pt">
                <Nav className="me-auto mr-2" activeKey={location.pathname}>
                    <Nav.Item onClick={() => setPendingActionOpen(prevCheck => !prevCheck)}>
                        <IconButton aria-label={notificationsLabel(100)} className={'pending-actions-bg'}>
                            <Badge color="success" badgeContent={pendingActions}>
                                <NotificationsNoneIcon className={'text-aie-blue'} />
                            </Badge>
                        </IconButton>
                    </Nav.Item>
                </Nav>
                {pendingActionOpen &&
                    <PendingActionPopup setPendingActionOpen={setPendingActionOpen} open={pendingActionOpen} />}

                <div className="nav-item dropdown">
                    <a onClick={handleProfileOpen} style={{ cursor: 'pointer' }}
                        className="nav-link d-flex align-items-center dropdown-toggle pointer-event"
                        data-toggle="dropdown"
                        data-caret="true">
                        {user && user.fullname}
                    </a>
                    <div className={`dropdown-menu dropdown-menu-right ${profileOpen ? 'show' : ''}`}>
                        {/*<div className="dropdown-header font-size-12pt"><strong>{user && user.username}</strong></div>*/}
                        {/*<div className="dropdown-header font-size-12pt"><strong>Account</strong></div>*/}
                        {/*<a className="dropdown-item" href="#">Edit Account</a>*/}
                        <a className="dropdown-item" onClick={e => handleLogout(e)} href="#">Logout</a>
                    </div>
                </div>
            </div>
        )
    }

    function MainNav() {

        return (
            <div className="navbar navbar-expand navbar-dark-pickled-bluewood bg-aie-blue
                        will-fade-background  mdk-header--shadow "
                id="default-navbar" data-primary="data-primary">
                <TopLeftSideBar />
                <TopRightSideBar />
            </div>
        )
    }

    function SecondaryNav(props: any) {
        useEffect(() => {
            document.querySelector("body")?.classList.add("has-secondary-nav");
            return () => {
                document.querySelector("body")?.classList.remove("has-secondary-nav");
            }
        }, [])

        return (

            <div className="navbar navbar-expand bg-dark-gray
                        will-fade-background  mdk-header--shadow " id="seconderyNavbar"
                style={{ marginBottom: '0px', paddingRight: '30px', paddingLeft: '15px' }}>
                <Nav className='nav navbar-nav'>

                    <Nav.Menu title="Dashboards">
                        {userRoles?.isGranted(Roles.DASHBOARD_EXECUTIVE) &&
                            <Nav.Item as={NavLink} href={`/projects/${props.project.id}/executive-dashboard`}>
                                Executive Dashboard
                            </Nav.Item>
                        }
                        {userRoles?.isGranted(Roles.DASHBOARD_PRODUCTION) &&
                            <Nav.Item as={NavLink} href={`/projects/${props.project.id}/production-dashboard`}>
                                Production Dashboard
                            </Nav.Item>
                        }
                        {userRoles?.isGranted(Roles.DASHBOARD_PERFORMANCE) &&
                            <Nav.Item as={NavLink} href={`/projects/${props.project.id}/performance-dashboard`}>
                                Performance Dashboard
                            </Nav.Item>
                        }
                        {userRoles?.isGranted(Roles.DASHBOARD_PRECAUTION) &&
                            <Nav.Item as={NavLink} href={`/projects/${props.project.id}/precautions-dashboard`}>
                                Precautions Dashboard
                            </Nav.Item>
                        }
                    </Nav.Menu>

                    <Nav.Menu title="Data Entry">
                        {userRoles?.checkGrantedByPattern(Roles.DATA_ENTRY) &&
                            <Nav.Item as={NavLink} href={`/projects/${props.project.id}/data-entry/forms`}>
                                Forms
                            </Nav.Item>
                        }
                        <Nav.Menu title="Loadsheets">
                            {userRoles?.checkGrantedByPattern('MNG_ROLE_LOADSHEET_PLANNING_') &&
                                <Nav.Item as={NavLink}
                                    href={`/projects/${props.project.id}/data-entry/planning-budget-load-sheet`}>
                                    Planning Loadsheet
                                </Nav.Item>}
                            {userRoles?.checkGrantedByPattern("MNG_ROLE_LOADSHEET_DOWNTIME_") &&
                                <Nav.Item as={NavLink}
                                    href={`/projects/${props.project.id}/data-entry/downtime-load-sheet`}>
                                    Downtime Loadsheet
                                </Nav.Item>}
                        </Nav.Menu>
                    </Nav.Menu>
                    {/* <Nav.Item as={NavLink} href={`/projects/${props.project.id}/document-register`}>
                        Documents
                    </Nav.Item> */}
                    <Nav.Menu title="Register">
                        <Nav.Item as={NavLink} href={`/projects/${props.project.id}/document-register`}>
                            Documents
                        </Nav.Item>
                        <Nav.Item as={NavLink} href={`/projects/${props.project.id}/downtime-register`}>
                            Downtime
                        </Nav.Item>
                    </Nav.Menu>
                    {userRoles?.checkGrantedByPattern("MNG_ROLE_REPORT_[A-Z]+_VIEW") &&
                        <Nav.Item as={NavLink} href={`/projects/${props.project.id}/reports`}>
                            Reports
                        </Nav.Item>
                    }
                </Nav>
            </div>
        )
    }

    return (
        <>
            <div id="header" className="mdk-header mdk-header--bg-dark  js-mdk-header mb-0">
                <div className="mdk-header__content justify-content-center">
                    <MainNav />
                    {project.id && <SecondaryNav project={project} />}
                </div>
            </div>
            {/*<Box sx={{width: '100%'}}><LinearProgress/></Box>*/}
        </>
    )
}